"use client";

/**
 * useApplyTransition — Hook für alle CTAs der Landing, die in den
 * Bewerbungs-Funnel führen.
 *
 * Rückgabe:
 *   - onClick: Handler, der das Signature-Overlay startet und die
 *     Default-Navigation des Links unterbindet. Bei Modifier-Keys
 *     (Ctrl/Cmd/Shift) bzw. mittlerer Maustaste lässt der Handler
 *     die Browser-Default-Aktion zu, damit "Open in new tab"
 *     weiterhin funktioniert.
 *
 * Verwendung:
 *
 *   const startApply = useApplyTransition();
 *   <Link href="/bewerben" onClick={startApply}>Bewerbung starten</Link>
 *
 * Der Overlay (ApplyTransitionOverlay) muss EINMAL global gemountet
 * sein — das passiert in page.tsx direkt unter LenisProvider/ScrollProgress.
 */

import { useCallback } from "react";
import { APPLY_START_EVENT } from "./ApplyTransitionOverlay";
import { track } from "@/app/bewerben/_components/analytics";

type AnyClickEvent =
  | React.MouseEvent<HTMLAnchorElement>
  | React.MouseEvent<HTMLButtonElement>
  | React.MouseEvent<HTMLElement>;

/** Ziehen `?job=<slug>` aus einem beliebigen href (relativ oder absolut). */
function jobSlugFromHref(href: string | null | undefined): string | undefined {
  if (!href) return undefined;
  const q = href.indexOf("?");
  if (q < 0) return undefined;
  const params = new URLSearchParams(href.slice(q + 1));
  const raw = params.get("job");
  if (!raw) return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 200) : undefined;
}

interface ApplyTransitionOpts {
  /** Tenant-Slug fuers Analytics-Beacon (apply_click). Ohne kein Beacon. */
  readonly tenantSlug?: string;
  /** Optionaler Job-Slug fallback wenn der Anker keinen ?job= traegt. */
  readonly jobSlug?: string;
}

export function useApplyTransition(opts: ApplyTransitionOpts = {}) {
  const { tenantSlug, jobSlug: fallbackJobSlug } = opts;
  return useCallback((e: AnyClickEvent) => {
    // Modifier oder Mittelklick -> Browser-Default (Open in new tab) erlauben.
    // button === 1 wird hier nicht abgegriffen (auxclick), aber Ctrl/Cmd/Shift
    // decken den Hauptanteil ab. Rechtklick (Context-Menu) feuert keinen click.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    if (typeof window === "undefined") return;
    // Karriere-Analytics Sub-Phase 2: apply_click-Beacon. job_slug bevorzugt
    // aus dem geklickten Anker (falls href=/bewerben?job=<slug>), sonst
    // Fallback-Prop. useBeacon:true, damit der Ping bei sofortiger Navigation
    // nicht verloren geht.
    if (tenantSlug) {
      const anchorHref =
        (e.currentTarget as HTMLAnchorElement | null)?.getAttribute?.("href") ??
        null;
      const jobSlug = jobSlugFromHref(anchorHref) ?? fallbackJobSlug;
      const payload: { event_type: "apply_click"; job_slug?: string } = {
        event_type: "apply_click",
      };
      if (jobSlug) payload.job_slug = jobSlug;
      track(tenantSlug, payload, { useBeacon: true });
    }
    window.dispatchEvent(new CustomEvent(APPLY_START_EVENT));
  }, [tenantSlug, fallbackJobSlug]);
}
